import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import type { OAuthProvider } from '@/lib/api/auth';
import { describeErrorText } from '@/lib/errors';
import { cn } from '@/lib/utils';

import { useOAuthSession } from '../hooks/useOAuthSession';

import { OAuthProviderLogo } from './OAuthProviderLogo';

const PROVIDERS: {
    id: OAuthProvider;
    label: string;
    name: string;
}[] = [
    {
        id: 'google',
        label: 'Continue with Google',
        name: 'Google',
    },
    {
        id: 'discord',
        label: 'Continue with Discord',
        name: 'Discord',
    },
    { id: 'x', label: 'Continue with X', name: 'X' },
    {
        id: 'telegram',
        label: 'Continue with Telegram',
        name: 'Telegram',
    },
];

interface SocialLoginButtonsProps {
    className?: string;
    layout?: 'stacked' | 'providerGrid';
    /** Surface a redirect/start failure to the caller (e.g. a toast). */
    onError?: (message: string) => void;
    /** Extra tiles rendered after the providers, in the same grid (providerGrid only). */
    trailing?: React.ReactNode;
}

/**
 * Provider buttons that kick off the v2-auth social login. Clicking redirects
 * the whole page to the provider's consent screen; the user returns to
 * `/auth/callback`. Reuses MoonHub's exact backend + OAuth clients.
 */
const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({
    className,
    layout = 'stacked',
    onError,
    trailing,
}) => {
    const { login, isLoading } = useOAuthSession();
    const [pending, setPending] = useState<OAuthProvider | null>(null);

    const handleLogin = async (provider: OAuthProvider) => {
        setPending(provider);
        try {
            await login(provider); // navigates away on success
        } catch (error) {
            setPending(null);
            // the callback text is toasted verbatim by the callers, so it has
            // to be presentable copy, never a provider SDK message.
            onError?.(describeErrorText(error, { operation: 'Sign-in' }));
        }
    };

    return (
        <div
            role={layout === 'providerGrid' ? 'group' : undefined}
            aria-label={
                layout === 'providerGrid' ? 'Social sign-in options' : undefined
            }
            className={cn(
                layout === 'providerGrid'
                    ? trailing
                        ? 'grid grid-cols-3 gap-2 sm:grid-cols-5'
                        : 'grid grid-cols-2 gap-2 sm:grid-cols-4'
                    : 'flex flex-col gap-2',
                className,
            )}
        >
            {PROVIDERS.map((provider) => (
                <Button
                    key={provider.id}
                    variant="outline"
                    size="sm"
                    disabled={isLoading}
                    onClick={() => void handleLogin(provider.id)}
                    aria-label={provider.label}
                    className={cn(
                        'justify-center gap-2',
                        layout === 'providerGrid' &&
                            'h-auto min-w-0 flex-col px-2 py-3 hover:border-primary/50 hover:bg-primary/5 hover:text-foreground dark:hover:bg-primary/5',
                    )}
                >
                    {pending === provider.id && (
                        <Loader2 size={14} className="animate-spin" />
                    )}
                    {layout === 'providerGrid' ? (
                        <>
                            <OAuthProviderLogo provider={provider.id} />
                            <span className="truncate text-xs font-medium">
                                {provider.name}
                            </span>
                        </>
                    ) : (
                        provider.label
                    )}
                </Button>
            ))}
            {layout === 'providerGrid' ? trailing : null}
        </div>
    );
};

export default SocialLoginButtons;
